import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { createProject } from '../../services/firestoreService';
import ProjectPaymentModal from '../../components/projects/ProjectPaymentModal';
import { getProjectPostingFee } from '../../services/projectPaymentService';

const PostProject = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);

  // State for form data
  const [formData, setFormData] = useState({
    title: '',
    client: '',
    industry: '',
    location: '',
    specialty: '',
    duration: '',
    budgetMin: '',
    budgetMax: '',
    description: '',
    requirements: [''],
    skills: [''],
    deadline: '',
    featured: false
  });

  // State for form validation
  const [errors, setErrors] = useState({});

  // State for payment modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [projectDataForPayment, setProjectDataForPayment] = useState(null);
  
  // Industry options
  const industryOptions = [
    'Technology',
    'Healthcare',
    'Financial Services',
    'Manufacturing',
    'Retail',
    'Energy',
    'Real Estate',
    'Consumer Goods',
    'Media & Entertainment',
    'Transportation & Logistics',
    'Other'
  ];
  
  // Specialty options
  const specialtyOptions = [
    'Investment Banking',
    'Private Equity',
    'Venture Capital',
    'Management Consultant/Advisory',
    'Accounting',
    'Valuation',
    'Legal',
    'Tax',
    'Other'
  ];
  
  // Handle input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Clear error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  
  // Handle requirements change
  const handleRequirementChange = (index, value) => {
    const updatedRequirements = [...formData.requirements];
    updatedRequirements[index] = value;
    setFormData({
      ...formData,
      requirements: updatedRequirements
    });
  };
  
  // Add new requirement field
  const addRequirement = () => {
    setFormData({
      ...formData,
      requirements: [...formData.requirements, '']
    });
  };
  
  // Remove requirement field
  const removeRequirement = (index) => {
    const updatedRequirements = [...formData.requirements];
    updatedRequirements.splice(index, 1);
    setFormData({
      ...formData,
      requirements: updatedRequirements
    });
  };
  
  // Handle skills change
  const handleSkillChange = (index, value) => {
    const updatedSkills = [...formData.skills];
    updatedSkills[index] = value;
    setFormData({
      ...formData,
      skills: updatedSkills
    });
  };
  
  // Add new skill field
  const addSkill = () => {
    setFormData({
      ...formData,
      skills: [...formData.skills, '']
    });
  };
  
  // Remove skill field
  const removeSkill = (index) => {
    const updatedSkills = [...formData.skills];
    updatedSkills.splice(index, 1);
    setFormData({
      ...formData,
      skills: updatedSkills
    });
  };
  
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) newErrors.title = 'Project title is required';
    if (!formData.client.trim()) newErrors.client = 'Client name is required';
    if (!formData.industry) newErrors.industry = 'Industry is required';
    if (!formData.location.trim()) newErrors.location = 'Location is required';
    if (!formData.specialty) newErrors.specialty = 'Specialty is required';
    if (!formData.duration.trim()) newErrors.duration = 'Duration is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.deadline) newErrors.deadline = 'Deadline is required';
    
    // Validate budget (optional — only validate if either field is filled)
    if (formData.budgetMin.trim() || formData.budgetMax.trim()) {
      if (!formData.budgetMin.trim() || !formData.budgetMax.trim()) {
        newErrors.budget = 'Please fill in both min and max if providing a budget range';
      } else if (parseInt(formData.budgetMin) >= parseInt(formData.budgetMax)) {
        newErrors.budget = 'Maximum budget must be greater than minimum budget';
      }
    }
    
    // Validate requirements
    const validRequirements = formData.requirements.filter(req => req.trim() !== '');
    if (validRequirements.length === 0) {
      newErrors.requirements = 'At least one requirement is required';
    }
    
    // Validate skills
    const validSkills = formData.skills.filter(skill => skill.trim() !== '');
    if (validSkills.length === 0) {
      newErrors.skills = 'At least one skill is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!currentUser) {
      alert('Please log in to post a project.');
      return;
    }

    if (validateForm()) {
      // Format the data for submission
      const projectData = {
        ...formData,
        budget: formData.budgetMin.trim() && formData.budgetMax.trim() ? `$${formData.budgetMin.trim()} - $${formData.budgetMax.trim()}` : '',
        budgetRange: formData.budgetMin.trim() && formData.budgetMax.trim() ? `${formData.budgetMin.trim()}-${formData.budgetMax.trim()}` : '',
        requirements: formData.requirements.filter(req => req.trim() !== ''),
        skills: formData.skills.filter(skill => skill.trim() !== ''),
        postedDate: new Date().toISOString().split('T')[0],
        status: 'active',
        featured: formData.featured,
        timeline: formData.duration,
        projectType: 'One-time',
        urgency: 'Medium',
        contactEmail: currentUser.email
      };

      // Store project data and show payment modal
      setProjectDataForPayment(projectData);
      setShowPaymentModal(true);
    } else {
      // Scroll to the first error
      const firstErrorField = Object.keys(errors)[0];
      const errorElement = document.getElementById(firstErrorField);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  // Handle successful project creation
  const handleProjectSuccess = async (projectData) => {
    setLoading(true);

    try {
      console.log('Creating project with data:', projectData);
      const projectId = await createProject(projectData, currentUser.uid);
      console.log('Project created with ID:', projectId);

      alert('Project posted successfully!');
      navigate(`/projects/${projectId}`);
    } catch (error) {
      console.error('Error creating project:', error);
      alert('Failed to create project posting. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Calculate posting fee
  const postingFee = getProjectPostingFee(formData.featured);
  const postingType = formData.featured ? 'FEATURED' : 'BASIC';
  
  return (
    <div className="bg-white">
      {/* Page Header */}
      <div className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* APN Logo */}
            <div className="flex justify-center mb-4">
              <img
                src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-white.jpg`}
                alt="APN Logo"
                className="h-16"
              />
            </div>
            <h1 className="text-3xl font-bold">Post a Project</h1>
          </div>
        </div>
      </div>

      {/* Post Project Form */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200 p-6">
            <form onSubmit={handleSubmit}>
              {/* Project Title */}
              <div className="mb-6">
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                  Project Title *
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue ${
                    errors.title ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g., M&A Due Diligence for Tech Acquisition"
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-500">{errors.title}</p>
                )}
              </div>
              
              {/* Client Name */}
              <div className="mb-6">
                <label htmlFor="client" className="block text-sm font-medium text-gray-700 mb-2">
                  Client Name *
                </label>
                <input
                  type="text"
                  id="client"
                  name="client"
                  value={formData.client}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue ${
                    errors.client ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g., Confidential or Company Name"
                />
                {errors.client && (
                  <p className="mt-1 text-sm text-red-500">{errors.client}</p>
                )}
              </div>
              
              {/* Industry and Location */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-2">
                    Industry *
                  </label>
                  <select
                    id="industry"
                    name="industry"
                    value={formData.industry}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue ${
                      errors.industry ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select Industry</option>
                    {industryOptions.map((industry, index) => (
                      <option key={index} value={industry}>
                        {industry}
                      </option>
                    ))}
                  </select>
                  {errors.industry && (
                    <p className="mt-1 text-sm text-red-500">{errors.industry}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2">
                    Location *
                  </label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue ${
                      errors.location ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., Remote or New York, NY"
                  />
                  {errors.location && (
                    <p className="mt-1 text-sm text-red-500">{errors.location}</p>
                  )}
                </div>
              </div>
              
              {/* Specialty and Duration */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="specialty" className="block text-sm font-medium text-gray-700 mb-2">
                    Specialty *
                  </label>
                  <select
                    id="specialty"
                    name="specialty"
                    value={formData.specialty}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue ${
                      errors.specialty ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select Specialty</option>
                    {specialtyOptions.map((specialty, index) => (
                      <option key={index} value={specialty}>
                        {specialty}
                      </option>
                    ))}
                  </select>
                  {errors.specialty && (
                    <p className="mt-1 text-sm text-red-500">{errors.specialty}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="duration" className="block text-sm font-medium text-gray-700 mb-2">
                    Duration *
                  </label>
                  <input
                    type="text"
                    id="duration"
                    name="duration"
                    value={formData.duration}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue ${
                      errors.duration ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., 4-6 weeks"
                  />
                  {errors.duration && (
                    <p className="mt-1 text-sm text-red-500">{errors.duration}</p>
                  )}
                </div>
              </div>
              
              {/* Budget Range */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Budget Range (USD) <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <div className="flex items-center space-x-2">
                  <div className="relative flex-1">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">$</span>
                    <input
                      type="number"
                      id="budgetMin"
                      name="budgetMin"
                      value={formData.budgetMin}
                      onChange={handleInputChange}
                      className={`w-full pl-8 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue ${
                        errors.budget ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Min"
                      min="0"
                    />
                  </div>
                  <span className="text-gray-500">to</span>
                  <div className="relative flex-1">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">$</span>
                    <input
                      type="number"
                      id="budgetMax"
                      name="budgetMax"
                      value={formData.budgetMax}
                      onChange={handleInputChange}
                      className={`w-full pl-8 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue ${
                        errors.budget ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Max"
                      min="0"
                    />
                  </div>
                </div>
                {errors.budget && (
                  <p className="mt-1 text-sm text-red-500">{errors.budget}</p>
                )}
              </div>
              
              {/* Project Description */}
              <div className="mb-6">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  Project Description *
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="5"
                  className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue ${
                    errors.description ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Provide a detailed description of the project..."
                ></textarea>
                {errors.description && (
                  <p className="mt-1 text-sm text-red-500">{errors.description}</p>
                )}
              </div>
              
              {/* Requirements */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Requirements *
                </label>
                {formData.requirements.map((requirement, index) => (
                  <div key={index} className="flex items-center mb-2">
                    <input
                      type="text"
                      value={requirement}
                      onChange={(e) => handleRequirementChange(index, e.target.value)}
                      className={`flex-1 px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue ${
                        errors.requirements ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder={`Requirement ${index + 1}`}
                    />
                    <button
                      type="button"
                      onClick={() => removeRequirement(index)}
                      className="ml-2 text-red-500 hover:text-red-700"
                      disabled={formData.requirements.length === 1}
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addRequirement}
                  className="mt-2 text-apn-blue hover:text-blue-700 flex items-center"
                >
                  <svg className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add Requirement
                </button>
                {errors.requirements && (
                  <p className="mt-1 text-sm text-red-500">{errors.requirements}</p>
                )}
              </div>
              
              {/* Skills */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Required Skills *
                </label>
                {formData.skills.map((skill, index) => (
                  <div key={index} className="flex items-center mb-2">
                    <input
                      type="text"
                      value={skill}
                      onChange={(e) => handleSkillChange(index, e.target.value)}
                      className={`flex-1 px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue ${
                        errors.skills ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder={`Skill ${index + 1}`}
                    />
                    <button
                      type="button"
                      onClick={() => removeSkill(index)}
                      className="ml-2 text-red-500 hover:text-red-700"
                      disabled={formData.skills.length === 1}
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addSkill}
                  className="mt-2 text-apn-blue hover:text-blue-700 flex items-center"
                >
                  <svg className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add Skill
                </button>
                {errors.skills && (
                  <p className="mt-1 text-sm text-red-500">{errors.skills}</p>
                )}
              </div>
              
              {/* Deadline */}
              <div className="mb-6">
                <label htmlFor="deadline" className="block text-sm font-medium text-gray-700 mb-2">
                  Application Deadline *
                </label>
                <input
                  type="date"
                  id="deadline"
                  name="deadline"
                  value={formData.deadline}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue ${
                    errors.deadline ? 'border-red-500' : 'border-gray-300'
                  }`}
                  min={new Date().toISOString().split('T')[0]}
                />
                {errors.deadline && (
                  <p className="mt-1 text-sm text-red-500">{errors.deadline}</p>
                )}
              </div>
              
              {/* Featured Project */}
              <div className="mb-6">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="featured"
                    name="featured"
                    checked={formData.featured}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-apn-blue focus:ring-apn-blue border-gray-300 rounded"
                  />
                  <label htmlFor="featured" className="ml-2 block text-sm text-gray-700">
                    Feature this project {postingFee > 0 && `(+$${postingFee})`}
                  </label>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Featured projects appear at the top of the project board and receive more visibility.
                  {postingFee > 0 && ` Additional fee: $${postingFee}`}
                </p>
              </div>
              
              {/* Submit Buttons */}
              <div className="flex justify-end space-x-4">
                <Link
                  to="/projects"
                  className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </Link>
                <button
                  type="submit"
                  className="px-6 py-2 bg-apn-blue text-white rounded-md hover:bg-blue-700 transition duration-200"
                >
                  {postingFee > 0 ? `Post Project ($${postingFee})` : 'Post Project (Free)'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Payment Modal */}
      {showPaymentModal && projectDataForPayment && (
        <ProjectPaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          projectData={projectDataForPayment}
          postingType={postingType}
          onSuccess={handleProjectSuccess}
        />
      )}
    </div>
  );
};

export default PostProject;
